import { Add, CloseOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridValidRowModel,
  useGridApiContext,
} from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useCustomSnackBar } from '../../../shared/SnackBar/useCustomSnackBar';
import { LoadingButton } from '@mui/lab';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  allDosingsCacheKey,
  allIngredientsCacheKey,
  updateIngredientDosings,
  useAllProductSizes,
  useIngredient,
} from '../../../apis/products-api';
import { DosingDto } from '@kotipizzagroup/kotipizza-products-api-client';
import { Transition } from '../../../shared/Transition/TransitionUp';

const PIZZA_CATEGORY_NAMES = ['Pizzat', 'Kotzonet', 'Monsterit', 'Luksuslankut', 'Minimonsterit'];

export type IngredientDosingDialogProps = {
  open: boolean;
  title?: string;
  ingredientId: number;
  onDialogClose?: () => void;
};

interface DosingSizeSelectProps extends GridRenderCellParams {
  disabled: boolean;
}

const DosingSizeSelect: React.FC<DosingSizeSelectProps> = (props) => {
  const apiRef = useGridApiContext();
  const existingProductSizes = Array.from(apiRef.current.getRowModels().values()).flatMap((x) => x.productSizeId);
  const { data: productSizes, isLoading: isProductSizesLoading } = useAllProductSizes();
  const { t } = useTranslation();

  const handleValueChange = (event: SelectChangeEvent<number>) => {
    const newValue = event.target.value;

    const row = apiRef.current.getRow(props.id);

    apiRef.current.setEditCellValue({
      id: props.id,
      field: props.field,
      value: newValue,
    });

    apiRef.current.updateRows([
      {
        ...row,
        productSizeId: newValue,
      },
    ]);
  };

  if (isProductSizesLoading || !productSizes) {
    return null;
  }

  const productSize = productSizes.find((size) => size.productSizeId === props.value);
  const availableProductSizes = productSizes.filter((size) => !existingProductSizes.includes(size.productSizeId));

  if (productSize) {
    availableProductSizes.push(productSize);
  }

  if (props.disabled) {
    return (
      <Box width="100%" padding="0.5em">
        {productSize ? productSize.combinedName : t('ingredients.dosings.chooseProductSize')} {`(id: ${props.value})`}
      </Box>
    );
  }

  if (!productSize && props.value) {
    return (
      <Box width="100%" padding="0.5em">
        {t('ingredients.dosings.missingProductSize')} {`(id: ${props.value})`}
      </Box>
    );
  }

  return (
    <Box width="100%" padding="0.25em">
      {(productSize || !props.value) && (
        <Select
          onOpen={() =>
            apiRef.current.startCellEditMode({
              id: props.id,
              field: props.field,
            })
          }
          onClose={() =>
            apiRef.current.stopCellEditMode({
              id: props.id,
              field: props.field,
            })
          }
          fullWidth
          defaultValue={0}
          value={props.value}
          onChange={handleValueChange}
          renderValue={(value: number) => {
            if (!value) {
              return t('ingredients.dosings.chooseProductSize');
            }

            return productSize?.combinedName;
          }}
        >
          {availableProductSizes.map((size) => (
            <MenuItem key={`${size.productSizeId}-${size.combinedName}`} value={size.productSizeId}>
              {size.combinedName}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
};

interface IDosingGridRow extends DosingDto {
  index: number;
}

const StyledDataGrid = styled(DataGrid)`
  /* Hide the up and down arrows from number inputs */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

export const IngredientDosingDialog = (props: IngredientDosingDialogProps): JSX.Element => {
  const [removedDosingIds, setRemovedDosingIds] = useState<number[]>([]);
  const [savingDosings, setSavingDosings] = useState(false);

  const { data: ingredient, isLoading, refetch: refetchIngredients } = useIngredient(props.ingredientId);
  const [gridRows, setGridRows] = useState<IDosingGridRow[]>([]);
  const snackBar = useCustomSnackBar();
  const queryClient = useQueryClient();
  const { data: productSizes, isLoading: isProductSizesLoading } = useAllProductSizes();
  const { t } = useTranslation();

  useEffect(() => {
    setGridRows(
      ingredient?.dosings?.map((dosing, index) => {
        return { ...dosing, index };
      }) || []
    );
  }, [ingredient]);

  const handleSaveDosings = async () => {
    try {
      setSavingDosings(true);
      await updateIngredientDosings(props.ingredientId, {
        removedDosingsIds: removedDosingIds,
        ingredientDosings: gridRows.map((row) => {
          return {
            ...row,
          };
        }),
      });
      snackBar.showSuccess(t('Dosings updated successfully'));
      await queryClient.invalidateQueries(allIngredientsCacheKey);
      await queryClient.invalidateQueries(allDosingsCacheKey);
      await refetchIngredients();
      props.onDialogClose?.();
    } catch (err) {
      console.error(err);
      snackBar.showError(t('Failed to update dosings'));
    } finally {
      setSavingDosings(false);
    }
  };

  const handleDeleteClick = useCallback(
    (id: GridRowId) => () => {
      const row = gridRows.find((row) => row.index === id);

      if (confirm(`${t('Are you sure you want to remove size')} (id: ${row?.dosingId})`)) {
        setGridRows(gridRows.filter((row) => row.index !== id));
        if (row?.dosingId) {
          setRemovedDosingIds((removedDosingIds) => [...removedDosingIds, row.dosingId as number]);
        }
      }
    },
    [gridRows, t]
  );

  const addNewDosingRow = () => {
    const lastIndex = [...gridRows].pop()?.index || 0;
    const newIndex = lastIndex + 1;

    const newRow: IDosingGridRow = {
      productSizeId: 0,
      ingredientId: props.ingredientId,
      priceWithVat: 0,
      dosingInGrams: 0,
      minQuantity: 0,
      maxQuantity: 3,
      active: true,
      index: newIndex,
    };

    setGridRows((oldGridRows) => [...oldGridRows, newRow]);
  };

  const handleSizes = () => {
    const pizzaSizes =
      productSizes
        ?.filter((size) => PIZZA_CATEGORY_NAMES.includes(size.productCategoryName))
        .map((size) => size.productSizeId) || [];

    const existingProductSizes: number[] =
      (ingredient?.dosings
        ?.map((dosing) => {
          return dosing.productSizeId;
        })
        .filter(Boolean) as number[]) || [];

    const filterArray = (pizzaSizes: number[], existingProductSizes: number[]) => {
      return pizzaSizes.filter((el: number) => {
        return existingProductSizes.indexOf(el) === -1;
      });
    };

    const filtered = filterArray(pizzaSizes, existingProductSizes);

    setGridRows((oldGridRows) => [
      ...oldGridRows,
      ...filtered.map((sizeId, index) => ({
        productSizeId: sizeId,
        ingredientId: props.ingredientId,
        priceWithVat: 0,
        dosingInGrams: 0,
        minQuantity: 0,
        maxQuantity: 3,
        active: true,
        index: existingProductSizes.length + index,
      })),
    ]);
  };

  const processRowUpdate = useCallback(
    (newRow: GridValidRowModel) => {
      const updatedRow = { ...newRow } as IDosingGridRow;
      setGridRows(gridRows.map((row) => (row.index === newRow.index ? updatedRow : row)));
      return updatedRow;
    },
    [gridRows]
  );

  const updateGridRows = (row: GridValidRowModel, value: string, field: keyof IDosingGridRow) => {
    const rowToUpdate = gridRows.find((r) => r.index === row.index);
    if (!rowToUpdate) return;
    const updatedRow = { ...rowToUpdate, [field]: value };
    setGridRows(gridRows.map((r) => (r.index === rowToUpdate.index ? updatedRow : r)));
  };

  const columns: GridColDef<GridValidRowModel>[] = [
    {
      field: 'productSizeId',
      headerName: t('Product category and size'),
      flex: 3,
      renderCell: (props) => <DosingSizeSelect {...props} disabled={false} />,
      renderEditCell: (props) => <DosingSizeSelect {...props} disabled={false} />,
      sortable: false,
      editable: true,
    },
    {
      field: 'priceWithVat',
      headerName: t('Price inc. VAT'),
      flex: 1,
      preProcessEditCellProps: (params) => {
        const hasError = isNaN(params.props.value);
        if (!hasError) updateGridRows(params.row, params.props.value, 'priceWithVat');
        return { ...params.props, error: hasError };
      },
      sortable: false,
      editable: true,
      type: 'number',
      renderCell: (params) => <TextField value={params.value} type="number" />,
    },
    {
      field: 'dosingInGrams',
      headerName: `${t('global.dosing')} (g)`,
      flex: 1,
      sortable: false,
      editable: true,
      type: 'number',
      preProcessEditCellProps: (params) => {
        const hasError = isNaN(params.props.value);
        if (!hasError) updateGridRows(params.row, params.props.value, 'dosingInGrams');
        return { ...params.props, error: hasError };
      },
      renderCell: (params) => <TextField value={params.value} type="number" />,
    },
    {
      field: 'minQuantity',
      headerName: 'Min',
      flex: 1,
      editable: true,
      sortable: false,
      type: 'number',
      preProcessEditCellProps: (params) => {
        const hasError = isNaN(params.props.value);
        if (!hasError) updateGridRows(params.row, params.props.value, 'minQuantity');
        return { ...params.props, error: hasError };
      },
      renderCell: (params) => <TextField value={params.value} type="number" />,
    },
    {
      field: 'maxQuantity',
      headerName: 'Max',
      flex: 1,
      editable: true,
      sortable: false,
      type: 'number',
      preProcessEditCellProps: (params) => {
        const hasError = isNaN(params.props.value);
        if (!hasError) updateGridRows(params.row, params.props.value, 'maxQuantity');
        return { ...params.props, error: hasError };
      },
      renderCell: (params) => <TextField value={params.value} type="number" />,
    },
    {
      field: 'dosingId',
      headerName: 'ID',
      flex: 1,
      sortable: false,
      editable: false,
      type: 'number',
      renderCell: (params) => {
        return params.value;
      },
    },
    {
      field: 'active',
      headerName: '',
      flex: 1,
      sortable: false,
      editable: false,
      renderCell: (params) => {
        return params.value ? '' : <Typography color="error">{t('ingredientDetails.inactive')}</Typography>;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('Actions'),
      cellClassName: 'actions',
      flex: 2,
      sortable: false,
      getActions: ({ id, row }: GridRowParams) => {
        const data: IDosingGridRow = row;

        const actions = [
          <GridActionsCellItem
            showInMenu={false}
            icon={
              <Link color="error" component="button" variant="body2">
                {t('global.delete')}
              </Link>
            }
            color="inherit"
            onClick={handleDeleteClick(id)}
            label={t('global.delete')}
          />,
        ];

        if (!data.active) {
          actions.unshift(
            <GridActionsCellItem
              showInMenu={false}
              icon={
                <Link color="primary" component="button" variant="body2">
                  {t('global.activate')}
                </Link>
              }
              color="inherit"
              onClick={() => updateGridRows(row, 'true', 'active')}
              label={t('global.activate')}
            />
          );
        }

        return actions;
      },
    },
  ];

  return (
    <Dialog open={props.open} TransitionComponent={Transition} keepMounted maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex">
          <Box alignItems="center" display="flex" flex="1">
            {props.title} - {ingredient?.name} ({gridRows.length || 0} {t('global.countShort')})
          </Box>
          <Box display="flex">
            <IconButton
              onClick={() => {
                props.onDialogClose?.();
              }}
              aria-label="close"
              color="primary"
            >
              <CloseOutlined />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <StyledDataGrid
          loading={isLoading}
          autoHeight
          rowHeight={65}
          hideFooter
          processRowUpdate={processRowUpdate}
          columns={columns}
          rows={gridRows}
          getRowId={(row) => row.index}
          disableRowSelectionOnClick
        />
        <Box marginTop="1em">
          <Button color="primary" variant="outlined" startIcon={<Add />} onClick={addNewDosingRow}>
            {t('Add dosing')}
          </Button>
          <Button
            disabled={isProductSizesLoading}
            color="primary"
            variant="outlined"
            startIcon={<Add />}
            onClick={handleSizes}
          >
            {t('Add all pizza products')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display="flex" padding="1em">
          <LoadingButton
            loading={savingDosings}
            color="primary"
            variant="contained"
            onClick={() => handleSaveDosings()}
          >
            {t('global.save')}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
